import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Info, AlertTriangle, Settings, Image as ImageIcon, Palette, Clock, HelpCircle } from 'lucide-react';
import { adminEnquetesAPI } from '../../utils/adminEnquetesAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';
import UploadImagemCapa from './UploadImagemCapa';
import PersonalizarFundoForm from './PersonalizarFundoForm';
import ModalRodape from './ModalRodape';
import Modal from './Modal';
import AjudaConfiguracoesGeraisModal from './AjudaConfiguracoesGeraisModal';

const inputClasses = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all';
const labelClasses = 'block text-sm font-medium text-gray-700 mb-1.5';

// A API devolve data_encerramento como ISO em UTC (o mysql2 reconstrói o
// DATETIME salvo respeitando o timezone:'-04:00' fixo configurado em
// database.js, e o JSON sempre serializa em UTC) — pra preencher os campos
// com o horário que foi realmente digitado (não o horário deslocado pra
// UTC), subtrai as 4h de volta e lê como se fosse UTC. Fuso fixo sem
// horário de verão permite essa conta direta, sem precisar de Intl/
// timezone database aqui. Aceita tanto a string ISO vinda da API quanto um
// Date puro (usado pelos atalhos abaixo, que calculam "daqui a N horas" a
// partir de agora) — a mesma conta serve pros dois casos.
function paraPartesCorumba(isoUtcOuData) {
  if (!isoUtcOuData) return { data: '', hora: '' };
  const data = isoUtcOuData instanceof Date ? isoUtcOuData : new Date(isoUtcOuData);
  if (Number.isNaN(data.getTime())) return { data: '', hora: '' };
  const comOffset = new Date(data.getTime() - 4 * 60 * 60 * 1000);
  const [dataParte, horaParte] = comOffset.toISOString().slice(0, 16).split('T');
  return { data: dataParte, hora: horaParte };
}

// Atalhos pros prazos mais comuns — cobre a maioria dos casos reais sem
// precisar tocar nos campos de data/hora nenhuma vez. Deliberadamente só
// deslocamentos relativos ("daqui a N horas/dias"), nunca "hoje às 23:59"
// ou parecido — isso exigiria calcular meia-noite em horário de Corumbá a
// partir de "hoje" no fuso de quem está digitando, e o -4h fixo acima não
// dá conta disso sem uma lib de timezone de verdade.
const ATALHOS_ENCERRAMENTO = [
  { label: 'Em 1 hora', horasAFrente: 1 },
  { label: 'Em 24 horas', horasAFrente: 24 },
  { label: 'Em 3 dias', horasAFrente: 72 },
  { label: 'Em 7 dias', horasAFrente: 168 }
];

// Três seções: "Geral" (título/descrição/status/ciclo), "Capa" e
// "Personalizar Fundo" — as duas últimas separadas pra dar espaço de
// verdade pra prévia/swatches, e nenhuma delas depende do botão "Salvar
// configurações" da aba Geral: upload/remoção de capa e escolha de fundo já
// salvam sozinhos (falam direto com a API assim que a pessoa clica).
const ABAS = [
  { id: 'geral', label: 'Geral', icon: Settings },
  { id: 'capa', label: 'Capa', icon: ImageIcon },
  { id: 'fundo', label: 'Personalizar Fundo', icon: Palette }
];

// Bloco "Configurações da enquete" — autocontido (busca seus próprios
// dados), usado dentro do modal "Configurar Enquete" (a partir de
// Gerenciar Enquetes, agora num modal bem mais largo — max-w-4xl — já que
// esse bloco cresceu bastante) e na página cheia de criação
// (EditorEnquete.js). Navegação responsiva: no desktop (sm:) vira uma barra
// lateral fixa em pé, dando a sensação de painel de configurações de
// verdade em vez de abas espremidas; no mobile continua em pílulas
// horizontais (o mesmo grupo de botões, só a direção do flex muda) — bem
// mais compacto ali, não cabe uma barra lateral de verdade numa tela
// pequena (ver discussão sobre padrões de navegação responsiva).
const ConfigurarEnqueteForm = ({ enqueteId, onSalvo }) => {
  const [enquete, setEnquete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aba, setAba] = useState('geral');
  const [mostrarAjuda, setMostrarAjuda] = useState(false);
  const { register, handleSubmit, reset, watch, setValue, formState: { isDirty } } = useForm();

  const carregar = useCallback(async () => {
    try {
      const dados = await adminEnquetesAPI.obter(enqueteId);
      setEnquete(dados);
      const { data: dataEncerramento, hora: horaEncerramento } = paraPartesCorumba(dados.data_encerramento);
      reset({
        titulo: dados.titulo,
        descricao: dados.descricao || '',
        modo_resultado: dados.modo_resultado,
        status: dados.status,
        data_encerramento_data: dataEncerramento,
        data_encerramento_hora: horaEncerramento
      });
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao carregar enquete');
    } finally {
      setLoading(false);
    }
  }, [enqueteId, reset]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const aplicarAtalhoEncerramento = (horasAFrente) => {
    const alvo = new Date(Date.now() + horasAFrente * 60 * 60 * 1000);
    const { data, hora } = paraPartesCorumba(alvo);
    setValue('data_encerramento_data', data, { shouldDirty: true });
    setValue('data_encerramento_hora', hora, { shouldDirty: true });
  };

  const onSalvarMetadados = async ({ data_encerramento_data: dataParte, data_encerramento_hora: horaParte, ...dados }) => {
    try {
      await adminEnquetesAPI.atualizar(enqueteId, {
        ...dados,
        data_encerramento: dataParte ? `${dataParte}T${horaParte || '00:00'}` : null
      });
      sucessoComHorario('Configurações salvas');
      await carregar();
      onSalvo?.();
    } catch (error) {
      if (error.response?.data?.code === 'SEM_PERGUNTAS') {
        toast.error('Adicione pelo menos uma pergunta antes de tornar a enquete pública');
      } else {
        toast.error(error.userMessage || 'Erro ao salvar');
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="loading-spinner" /></div>;
  }

  if (!enquete) {
    return <p className="text-gray-500 text-center py-8">Enquete não encontrada.</p>;
  }

  const totalPerguntasAtivas = (enquete.perguntas || []).filter((p) => p.ativa).length;
  const podeFicarPublica = totalPerguntasAtivas > 0;
  const statusAtual = watch('status');

  return (
    <>
    <div className="sm:flex sm:items-start sm:gap-6">
      <div className="flex sm:flex-col gap-1 bg-gray-100 rounded-xl p-1 mb-5 sm:mb-0 sm:w-48 sm:flex-shrink-0 sm:sticky sm:top-0">
        {ABAS.map(({ id, label, icon: Icone }) => (
          <button
            key={id}
            type="button"
            onClick={() => setAba(id)}
            className={`flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-2 text-sm font-medium py-2 sm:py-2.5 sm:px-3 rounded-lg transition-colors ${
              aba === id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icone className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{label}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 min-w-0">
      {aba === 'geral' && (
        <form onSubmit={handleSubmit(onSalvarMetadados)} className="space-y-6">
          {/* Seção: identidade */}
          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Identidade</h3>

            <div>
              <label htmlFor="configurar-enquete-titulo" className={labelClasses}>Título</label>
              <input id="configurar-enquete-titulo" {...register('titulo', { required: true })} className={inputClasses} />
            </div>

            <div>
              <label htmlFor="configurar-enquete-descricao" className={labelClasses}>Descrição</label>
              <textarea id="configurar-enquete-descricao" {...register('descricao')} className={inputClasses} rows={2} />
            </div>
          </section>

          {/* Seção: ciclo de vida — visibilidade não é editável por aqui, é
              controlada pelo toggle na linha da lista (Gerenciar Enquetes) */}
          <section className="space-y-4 pt-1 border-t border-gray-100">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 pt-4">Status</h3>

            <div>
              <label htmlFor="configurar-enquete-status" className={labelClasses}>Status</label>
              <select id="configurar-enquete-status" {...register('status')} className={inputClasses}>
                <option value="rascunho">Rascunho</option>
                <option value="publicada">Publicada</option>
                <option value="encerrada">Encerrada</option>
              </select>
            </div>

            {!podeFicarPublica && (
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Essa enquete ainda não tem nenhuma pergunta — só pode ficar pública depois de adicionar pelo menos uma (use "Editar Perguntas").
              </div>
            )}

            {podeFicarPublica && statusAtual === 'rascunho' && (
              <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Com status "Rascunho", tornar essa enquete pública cria um link de prévia — dá pra revisar as perguntas antes de publicar, mas votação e resultado ficam bloqueados até o status virar "Publicada".
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="configurar-enquete-modo-resultado" className={labelClasses}>Mostrar resultado</label>
                <select id="configurar-enquete-modo-resultado" {...register('modo_resultado')} className={inputClasses}>
                  <option value="tempo_real">Tempo real (assim que votar)</option>
                  <option value="apos_encerramento">Só após encerrar</option>
                  <option value="nao_mostrar">Nunca mostrar</option>
                </select>

                <button
                  type="button"
                  onClick={() => setMostrarAjuda(true)}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 border-2 border-blue-300 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors animate-pulse-blue"
                >
                  <HelpCircle className="w-4 h-4" /> Dúvidas sobre esta tela
                </button>
              </div>
              <div>
                <label className={labelClasses}>Data e hora de encerramento</label>

                {/* Atalhos primeiro — cobrem o caso comum sem precisar
                    tocar nos campos abaixo nenhuma vez. */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {ATALHOS_ENCERRAMENTO.map(({ label, horasAFrente }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => aplicarAtalhoEncerramento(horasAFrente)}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:border-[#40A1E6] hover:text-[#40A1E6] transition-colors"
                    >
                      <Clock className="w-3 h-3" /> {label}
                    </button>
                  ))}
                </div>

                {/* Data e hora em campos nativos separados — mais simples
                    de preencher (e mais consistente entre navegadores) do
                    que um único <input type="datetime-local">, que empacota
                    dia/mês/ano/hora/minuto num único widget segmentado. */}
                {/* Um <label> visual só, cobrindo os dois campos — cada
                    input ganha o próprio aria-label (não renderiza nada,
                    não muda o visual) porque "Data e hora de encerramento"
                    sozinho não distingue qual dos dois é qual pra leitor de
                    tela/teste. */}
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" aria-label="Data de encerramento" {...register('data_encerramento_data')} className={inputClasses} />
                  <input type="time" aria-label="Hora de encerramento" {...register('data_encerramento_hora')} className={inputClasses} />
                </div>

                <p className="text-xs text-gray-400 mt-1">Ao passar desse horário, a enquete fecha sozinha (status vira "Encerrada"). Horário de Corumbá (UTC-4).</p>
              </div>
            </div>
          </section>

          <ModalRodape semSangria>
            <div className="flex flex-col items-center gap-1.5">
              <button type="submit" disabled={!isDirty} className="btn-primary disabled:opacity-40">
                Salvar configurações
              </button>
              {!isDirty && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Info className="w-3.5 h-3.5" /> Nenhuma alteração pendente
                </span>
              )}
            </div>
          </ModalRodape>
        </form>
      )}

      {aba === 'capa' && (
        <UploadImagemCapa
          enqueteId={enqueteId}
          imagemCapa={enquete.imagem_capa}
          metadataImagemCapa={enquete.metadata_imagem_capa}
          imagemCapaMobile={enquete.imagem_capa_mobile}
          metadataImagemMobile={enquete.metadata_imagem_mobile}
          onChange={carregar}
        />
      )}

      {aba === 'fundo' && (
        <PersonalizarFundoForm
          enqueteId={enqueteId}
          fundoTipo={enquete.fundo_tipo}
          fundo={enquete.fundo}
          metadataFundo={enquete.metadata_fundo}
          onChange={carregar}
        />
      )}
      </div>
    </div>

    {mostrarAjuda && (
      <Modal
        titulo="Dúvidas sobre esta tela"
        onFechar={() => setMostrarAjuda(false)}
        maxWidth="max-w-lg"
        rodapeFechar
      >
        <AjudaConfiguracoesGeraisModal />
      </Modal>
    )}
    </>
  );
};

export default ConfigurarEnqueteForm;
